import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import FormModal from 'LuxKit/Modal/FormModal'
import Group from 'components/utils/Group'
import TextInput from 'components/Common/Form/Input/TextInput'
import TextArea from 'components/Common/Form/Input/TextArea'
import Select from 'components/Common/Form/Input/Select'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineBanIcon from 'LuxKit/Icons/line/LineBanIcon'
import { createWeddingGuest, createWeddingMessage, WeddingRsvpStatus } from 'weddings/api/weddings'
import type { Wedding } from 'weddings/api/weddings'
import Expander from 'components/utils/Expander'
import moment from 'moment'
import { DAY_MONTH_NAME, DAY_MONTH_NAME_YEAR } from 'constants/dateFormats'
import { getPublicMessageBoardContentBlock, getRsvpFormContentBlock } from 'weddings/lib/utils'
import InputText from 'LuxKit/Typography/InputText'

const Content = styled(Group)`
  .flex-1 {
    flex: 1;
  }
`

const RsvpOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(8)};
`

const RsvpOption = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(4)};
  padding: ${rem(8)} ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};

  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &.selected {
    border: 2px solid ${props => props.theme.palette.brand.primary.normal};
  }
`

const MESSAGE_MAX_LENGTH = 200
const MAX_ADDITIONAL_GUESTS = 9

interface Props {
  wedding: Wedding
}

function WeddingRsvpModal(props: Props) {
  const { wedding } = props
  const intl = useIntl()

  const rsvpContent = useMemo(() => {
    return getRsvpFormContentBlock(wedding.contentBlocks)?.content
  }, [wedding.contentBlocks])

  const hasPublicMessageBoard = useMemo(() => {
    return !!getPublicMessageBoardContentBlock(wedding.contentBlocks)
  }, [wedding.contentBlocks])

  const { resolve } = useModalElementContext<boolean>()
  const givenName = useAppSelector(state => state.auth.account.givenName) ?? ''
  const surname = useAppSelector(state => state.auth.account.surname) ?? ''

  const [rsvpStatus, setRsvpStatus] = useState<WeddingRsvpStatus>(WeddingRsvpStatus.CONFIRMED)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleStatusChange = useCallback((value: WeddingRsvpStatus) => {
    setRsvpStatus(value)
  }, [])

  const handleSubmit = useCallback(async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)

    const firstName = (data.get('firstName') as string).trim()
    const lastName = (data.get('lastName') as string).trim()
    const additionalGuestCountRaw = data.get('additionalGuestCount') as string | null
    const dietaryNeeds = (data.get('dietaryNeeds') as string | null)?.trim() || undefined
    const songRequest = (data.get('songRequest') as string | null)?.trim() || undefined
    const publicNote = (data.get('publicNote') as string | null)?.trim() || undefined
    const additionalGuestCount = additionalGuestCountRaw ? parseInt(additionalGuestCountRaw, 10) : 0

    try {
      setIsSubmitting(true)
      setError(null)

      const guest = await createWeddingGuest(wedding.id, {
        firstName,
        lastName,
        rsvpStatus,
        additionalGuestCount,
        dietaryNeeds,
        songRequest,
      })

      if (publicNote && hasPublicMessageBoard) {
        await createWeddingMessage(wedding.id, {
          authorFirstName: firstName,
          authorLastName: lastName,
          content: publicNote,
          guestId: guest.id,
        })
      }
      resolve(rsvpStatus === WeddingRsvpStatus.CONFIRMED)
    } catch {
      setError(intl.formatMessage({ defaultMessage: 'Failed to send your RSVP. Please try again.', id: 'Bx9LPyp9_B9HIvVk6Yg0', description: 'Error message shown when a wedding RSVP submission fails.' }))
    } finally {
      setIsSubmitting(false)
    }
  }, [wedding, rsvpStatus, hasPublicMessageBoard, resolve, intl])

  const handleClose = useCallback(() => {
    resolve(false)
  }, [resolve])

  const { limitDate, weddingDayLabel } = useMemo(() => {
    return {
      weddingDayLabel: moment(wedding.eventStartDate).format(DAY_MONTH_NAME),
      limitDate: moment(wedding.eventStartDate).subtract(1, 'day').format(DAY_MONTH_NAME_YEAR),
    }
  }, [wedding.eventStartDate])

  return (
    <FormModal
      title={intl.formatMessage(
        { defaultMessage: 'Will you be there on {weddingDayLabel}?', id: 'fpghGNV4yAYp-r6ZZWVl', description: 'Title for the RSVP modal asking whether a guest will attend the wedding.' },
        { weddingDayLabel },
      )}
      subtitle={intl.formatMessage(
        { defaultMessage: 'Please RSVP by {limitDate}', id: 'HHwC1FFicmIAHpswP_Z5', description: 'Subtitle for the wedding RSVP modal with the response due date.' },
        { limitDate },
      )}
      primaryActionText={isSubmitting ?
          intl.formatMessage({ defaultMessage: 'Sending...', id: 'hCN8XFwYd5N9IUTMruqL', description: 'Loading state text for the wedding RSVP submit button.' }) :
          intl.formatMessage({ defaultMessage: 'Send RSVP', id: 'usuuW2MuzdUuKdhnw_-t', description: 'Primary action button for submitting a wedding RSVP.' })}
      isPrimaryActionDisabled={isSubmitting}
      primaryActionType="submit"
      onSubmit={handleSubmit}
      onClose={handleClose}
    >
      <Content direction="vertical" gap={20}>
        <Group direction="vertical" gap={8}>
          <InputText variant="label">
            {intl.formatMessage({ defaultMessage: 'Are you coming?', id: 'gdN2d0aXqS9VyUX8t6-M', description: 'Label for the attendance choice field in the wedding RSVP modal.' })}
          </InputText>
          <RsvpOptionsGrid>
            <RsvpOption
              className={clsx({ selected: rsvpStatus === WeddingRsvpStatus.CONFIRMED })}
              onClick={() => handleStatusChange(WeddingRsvpStatus.CONFIRMED)}
            >
              <LineCheckIcon size="M" />
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({ defaultMessage: "I'll be there", id: '9vRHpCYTy_RuUqWZLAme', description: 'Option label for confirming attendance in the wedding RSVP modal.' })}
              </BodyText>
            </RsvpOption>
            <RsvpOption
              className={clsx({ selected: rsvpStatus === WeddingRsvpStatus.DECLINED })}
              onClick={() => handleStatusChange(WeddingRsvpStatus.DECLINED)}
            >
              <LineBanIcon size="M" />
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({ defaultMessage: "I can't make it", id: 'oy3e_YDRMK1zdxzpPldP', description: 'Option label for declining attendance in the wedding RSVP modal.' })}
              </BodyText>
            </RsvpOption>
          </RsvpOptionsGrid>
        </Group>

        <Group direction="vertical" gap={4} tabletGap={8}>
          <Group direction="horizontal" gap={4} tabletGap={8}>
            <TextInput
              name="firstName"
              label={intl.formatMessage({ defaultMessage: 'First name', id: 'NItn4mtEzUZJhSw79rGv', description: 'Label for guest first name input in the wedding RSVP modal.' })}
              placeholder={intl.formatMessage({ defaultMessage: 'First name', id: 'YoRxh1LcHtiDv7GgIiq5', description: 'Placeholder for guest first name input in the wedding RSVP modal.' })}
              defaultValue={givenName}
              required
              requiredErrorMessage={intl.formatMessage({ defaultMessage: 'First name is required', id: 'fKxGxWfskf8zyIVy-pVx', description: 'Validation message when RSVP first name is missing.' })}
              className="flex-1"
            />
            <TextInput
              name="lastName"
              label={intl.formatMessage({ defaultMessage: 'Last name', id: 'Ncymj6iB5j8YsanpeaMn', description: 'Label for guest last name input in the wedding RSVP modal.' })}
              placeholder={intl.formatMessage({ defaultMessage: 'Last name', id: 'HYCQp2y-r7v0tgjKqGiu', description: 'Placeholder for guest last name input in the wedding RSVP modal.' })}
              defaultValue={surname}
              required
              requiredErrorMessage={intl.formatMessage({ defaultMessage: 'Last name is required', id: 'O15ai-lyN6kMGB4QXcQy', description: 'Validation message when RSVP last name is missing.' })}
              className="flex-1"
            />
          </Group>

          <Expander expanded={rsvpStatus === WeddingRsvpStatus.CONFIRMED}>
            <Group direction="vertical" gap={4} tabletGap={8} tabletDirection="horizontal">
              {rsvpContent?.numberOfGuests && (
                <Select
                  name="additionalGuestCount"
                  label={intl.formatMessage({ defaultMessage: 'Number of additional guests', id: 'nl0TRQBR9mgThN7nE_Mm', description: 'Label for additional guest count in the wedding RSVP modal.' })}
                  defaultValue="0"
                  required={rsvpStatus === WeddingRsvpStatus.CONFIRMED}
                  requiredErrorMessage={intl.formatMessage({ defaultMessage: 'Number of additional guests is required', id: '4e3vK2XMSSvdYLfJuZZh', description: 'Validation message when additional guest count is missing.' })}
                  className="flex-1"
                >
                  {Array.from({ length: MAX_ADDITIONAL_GUESTS + 1 }, (_, i) => (
                    <option key={i} value={i}>
                      {i === 0 ?
                          intl.formatMessage({ defaultMessage: 'Just myself', id: 'LSUUwjyEOzT4kFSmb7Wt', description: 'Select option for no additional guests in the wedding RSVP modal.' }) :
                          intl.formatMessage({ defaultMessage: '+{guestCount}', id: 'Al8QXZc_kTG9n70bmuR1', description: 'Select option for additional guest count in the wedding RSVP modal.' }, { guestCount: i })}
                    </option>
                  ))}
                </Select>
              )}

              {rsvpContent?.dietaryRequirements && (
                <TextInput
                  name="dietaryNeeds"
                  label={intl.formatMessage({ defaultMessage: 'Dietary needs', id: 'QAAdfFEZjsBXULbjMTnG', description: 'Label for dietary needs input in the wedding RSVP modal.' })}
                  placeholder={intl.formatMessage({ defaultMessage: 'Gluten-free, vegan, etc.', id: 'HFvkYGxGbRqhLxvzofKl', description: 'Placeholder examples for dietary needs in the wedding RSVP modal.' })}
                  className="flex-1"
                />
              )}
            </Group>

            {rsvpContent?.songRequests && (
              <TextInput
                name="songRequest"
                label={intl.formatMessage({ defaultMessage: 'Song request for the dance floor', id: '_T33CKBosZ76ZgSLreui', description: 'Label for song request input in the wedding RSVP modal.' })}
                placeholder={intl.formatMessage({ defaultMessage: 'We’ll keep the dance floor packed', id: 'ARvEexxIuiC9trfvvGj8', description: 'Placeholder for song request input in the wedding RSVP modal.' })}
              />
            )}

            {hasPublicMessageBoard && (
              <Group direction="vertical" gap={4}>
                <TextArea
                  name="publicNote"
                  label={intl.formatMessage(
                    { defaultMessage: 'A public note for {partnerOneFirstName} & {partnerTwoFirstName}', id: 'B0l2ddgXtgJnOr2yHDQU', description: 'Label for public note input in the wedding RSVP modal.' },
                    { partnerOneFirstName: wedding.partnerOneFirstName, partnerTwoFirstName: wedding.partnerTwoFirstName },
                  )}
                  placeholder={intl.formatMessage({ defaultMessage: 'This will be displayed in the message board.', id: '2w3E9wp_CzztLK83QVdh', description: 'Placeholder explaining where a wedding RSVP public note appears.' })}
                  maxLength={MESSAGE_MAX_LENGTH}
                  rows={4}
                  helpText={intl.formatMessage(
                    { defaultMessage: 'Max. {maxLength} characters', id: 'TmU7RTJCrILtZ-VwS-Oq', description: 'Help text showing maximum character count for wedding RSVP public note.' },
                    { maxLength: MESSAGE_MAX_LENGTH },
                  )}
                />
              </Group>
            )}
          </Expander>
        </Group>

        {error && <BodyText variant="small" colour="critical">{error}</BodyText>}
      </Content>
    </FormModal>
  )
}

export default WeddingRsvpModal
